import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import RevenueChart from './components/RevenueChart';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import EventPerformance from './components/EventPerformance';

const HostDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState('7d');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const metricsData = [
    {
      title: 'Total Revenue',
      value: '$47,250',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      trend: '$8,420 this week'
    },
    {
      title: 'Upcoming Events',
      value: '8',
      change: '+2',
      changeType: 'positive',
      icon: 'Calendar',
      trend: '3 this week'
    },
    {
      title: 'Tickets Sold',
      value: '1,247',
      change: '+18.2%',
      changeType: 'positive',
      icon: 'Ticket',
      trend: '234 this week'
    },
    {
      title: 'Total Attendees',
      value: '3,892',
      change: '+5.8%',
      changeType: 'positive',
      icon: 'Users',
      trend: '567 this month'
    }
  ];

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header Section */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
                <Icon name="Clock" size={16} />
                <span>{formatTime(currentTime)} • {formatDate(currentTime)}</span>
              </div>
              <h1 className="text-3xl font-bold text-foreground">Host Dashboard</h1>
              <p className="text-lg text-muted-foreground mt-1">
                Welcome back! Here's what's happening with your events.
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm">
                <Icon name="Download" size={16} className="mr-2" />
                Export Data
              </Button>
              <Button variant="default" asChild>
                <Link to="/event-creation">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Event
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricsData?.map((metric, index) => (
            <MetricsCard
              key={index}
              title={metric?.title}
              value={metric?.value}
              change={metric?.change}
              changeType={metric?.changeType}
              icon={metric?.icon}
              trend={metric?.trend}
            />
          ))}
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Revenue Chart - Takes 2 columns */}
          <div className="lg:col-span-2">
            <RevenueChart />
          </div>
          
          {/* Quick Actions - Takes 1 column */}
          <div>
            <QuickActions />
          </div>
        </div>

        {/* Secondary Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Event Performance */}
          <EventPerformance />
          
          {/* Recent Activity */}
          <RecentActivity />
        </div>

        {/* Real-time Status Bar */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-foreground">System Status: Online</span>
              </div>
              
              <div className="hidden md:flex items-center space-x-6 text-sm text-muted-foreground">
                <span>Last sync: {formatTime(currentTime)}</span>
                <span>•</span>
                <span>5 active events</span>
                <span>•</span>
                <span>23 pending check-ins</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              
              <Button variant="ghost" size="sm">
                <Icon name="Bell" size={16} />
                <span className="ml-2 bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                  3
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/event-management"
              className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:shadow-md transition-smooth"
            >
              <Icon name="Settings" size={20} className="text-primary" />
              <div>
                <h4 className="font-medium text-foreground">Event Management</h4>
                <p className="text-sm text-muted-foreground">Manage all events</p>
              </div>
            </Link>
            
            <Link
              to="/attendee-management"
              className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:shadow-md transition-smooth"
            >
              <Icon name="Users" size={20} className="text-primary" />
              <div>
                <h4 className="font-medium text-foreground">Attendees</h4>
                <p className="text-sm text-muted-foreground">Manage guest lists</p>
              </div>
            </Link>
            
            <Link
              to="/host-dashboard"
              className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:shadow-md transition-smooth"
            >
              <Icon name="BarChart3" size={20} className="text-primary" />
              <div>
                <h4 className="font-medium text-foreground">Analytics</h4>
                <p className="text-sm text-muted-foreground">Detailed reports</p>
              </div>
            </Link>
            
            <Link
              to="/event-creation"
              className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:shadow-md transition-smooth"
            >
              <Icon name="Plus" size={20} className="text-primary" />
              <div>
                <h4 className="font-medium text-foreground">Create Event</h4>
                <p className="text-sm text-muted-foreground">New event setup</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostDashboard;